/**
 * The RydRes mark: a sport steering wheel (flat-bottomed rim, square hub, three spokes) with the
 * brand R cut out of the hub, so whatever is behind the mark shows through the letter. Drawn in a
 * 100-unit box in three weights: `full` from 41 px, `small` (thicker, R kept) for 21-40 px and
 * `tiny` (no R: an R in a ring reads as ® at that size) up to 20 px.
 *
 * One source for the inline logo (logo.tsx) and every icon file (scripts/render-icons.mjs), so this
 * module has no imports.
 */
export type MarkWeight = 'full' | 'small' | 'tiny';
/** light / dark: the gradient for that background; css: the theme's --t-mark-* tokens; mono: one colour. */
export type MarkTheme = 'light' | 'dark' | 'css' | 'mono';

const R_PATH =
  'M18 46V18h13c7 0 11 4 11 9.5 0 4-2.4 7-6 8.3L44 46h-8l-6.6-9H25v9h-7zm7-15h5.6c3 0 4.7-1.4 4.7-3.6S33.6 24 30.6 24H25v7z';

/** Deep blue → royal blue → teal → turquoise, and the turquoise 12 o'clock marker; dark uses the light stops. */
export const MARK_COLORS = {
  light: { stops: ['#1e3a8a', '#2563eb', '#0d9488', '#14b8a6'], tip: '#2dd4bf' },
  dark: { stops: ['#93c5fd', '#60a5fa', '#2dd4bf', '#5eead4'], tip: '#ccfbf1' },
} as const;
const OFFSETS = [0, 0.48, 0.84, 1];
/** The app icon's square: the brand gradient. */
export const ICON_STOPS = ['#1e3a8a', '#2563eb', '#0f766e'] as const;

/** sw rim stroke, rr rim radius, flat the flat bottom's y, hw/hh half the hub, sp/spRim spoke width at hub/rim, rh R height. */
const WEIGHTS: Record<MarkWeight, { sw: number; rr: number; flat: number; hw: number; hh: number; sp: number; spRim: number; rh: number }> = {
  full: { sw: 9, rr: 41.5, flat: 82, hw: 20, hh: 18.5, sp: 11, spRim: 7, rh: 23 },
  small: { sw: 12.5, rr: 39.75, flat: 80, hw: 22, hh: 20, sp: 15, spRim: 12, rh: 25 },
  tiny: { sw: 15, rr: 38.5, flat: 79, hw: 14, hh: 12, sp: 17, spRim: 15, rh: 0 },
};
const HUB_Y = 48.5;

const n = (x: number) => Math.round(x * 100) / 100;

export const weightFor = (px: number): MarkWeight => (px <= 20 ? 'tiny' : px <= 40 ? 'small' : 'full');

/** The wheel's shapes, the cut-out R and the marker, for one weight. */
function shapes(weight: MarkWeight) {
  const g = WEIGHTS[weight];
  const dx = Math.sqrt(g.rr * g.rr - (g.flat - 50) ** 2);
  const side = (dir: 1 | -1) =>
    `<path d="M${n(50 + dir * g.rr)} ${n(HUB_Y - g.spRim / 2)}L${n(50 + dir * (g.hw - 2))} ${n(HUB_Y - g.sp / 2)}V${n(HUB_Y + g.sp / 2)}L${n(50 + dir * g.rr)} ${n(HUB_Y + g.spRim / 2)}Z"/>`;
  const s = g.rh / 28;
  const mw = weight === 'full' ? 5.5 : 7;
  return {
    rim: `<path d="M${n(50 - dx)} ${g.flat}A${g.rr} ${g.rr} 0 1 1 ${n(50 + dx)} ${g.flat}Z" fill="none" stroke-width="${g.sw}" stroke-linejoin="round"/>`,
    body:
      `<rect x="${50 - g.hw}" y="${HUB_Y - g.hh}" width="${g.hw * 2}" height="${g.hh * 2}" rx="${n(Math.min(g.hw, g.hh) * 0.68)}"/>` +
      side(-1) +
      side(1) +
      `<path d="M${n(50 - g.sp / 2 - 1)} ${HUB_Y + g.hh - 2}H${n(50 + g.sp / 2 + 1)}L${n(50 + g.spRim / 2)} ${g.flat}H${n(50 - g.spRim / 2)}Z"/>`,
    letter: g.rh ? `<path transform="translate(${n(50 - 31 * s + g.rh * 0.02)} ${n(HUB_Y - 32 * s)}) scale(${n(s * 1000) / 1000})" d="${R_PATH}"/>` : '',
    tip: { x: 50 - mw / 2, y: n(50 - g.rr - (g.sw + 1) / 2), w: mw, h: g.sw + 1 },
  };
}

const cutOut = (id: string, letter: string) =>
  letter ? `<mask id="${id}m" maskUnits="userSpaceOnUse" x="0" y="0" width="100" height="100"><rect width="100" height="100" fill="#fff"/><g fill="#000">${letter}</g></mask>` : '';

/**
 * The mark alone, transparent around it. `id` keeps gradient and mask ids unique when several marks
 * share a page. Without `title` the mark is hidden from screen readers (put the name beside it).
 */
export function markSvg({
  size,
  theme = 'light',
  weight = weightFor(size),
  mono = '#ffffff',
  tip = weight !== 'tiny',
  id = 'rm',
  title,
}: { size: number; theme?: MarkTheme; weight?: MarkWeight; mono?: string; tip?: boolean; id?: string; title?: string }): string {
  const sh = shapes(weight);
  const stop = (i: number) =>
    theme === 'css' ? `style="stop-color:var(--t-mark-${i + 1})"` : `stop-color="${MARK_COLORS[theme === 'dark' ? 'dark' : 'light'].stops[i]}"`;
  const paint = theme === 'mono' ? mono : `url(#${id}g)`;
  const tipFill = theme === 'css' ? 'style="fill:var(--t-mark-tip)"' : `fill="${MARK_COLORS[theme === 'dark' ? 'dark' : 'light'].tip}"`;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="${size}" height="${size}"${title ? ` role="img" aria-label="${title}"` : ' aria-hidden="true"'}>` +
    '<defs>' +
    (theme === 'mono'
      ? ''
      : `<linearGradient id="${id}g" x1="12" y1="6" x2="88" y2="94" gradientUnits="userSpaceOnUse">${OFFSETS.map((o, i) => `<stop offset="${o}" ${stop(i)}/>`).join('')}</linearGradient>`) +
    cutOut(id, sh.letter) +
    '</defs>' +
    `<g${sh.letter ? ` mask="url(#${id}m)"` : ''} fill="${paint}" stroke="${paint}">${sh.rim}<g stroke="none">${sh.body}</g></g>` +
    (tip && theme !== 'mono' ? `<rect x="${sh.tip.x}" y="${sh.tip.y}" width="${sh.tip.w}" height="${sh.tip.h}" rx="1.4" ${tipFill}/>` : '') +
    '</svg>'
  );
}

/**
 * The app icon: the gradient square with the wheel in white, the R showing the gradient through the
 * hub. `inset` is the margin around the wheel's box as a share of the side; `round` rounds the
 * corners (stores and phones mask a square icon themselves, so leave it off for those). `square:
 * false` leaves only the white wheel on transparent, for an Android adaptive icon's foreground.
 */
export function appIconSvg({
  size,
  inset = 0.14,
  round = true,
  square = true,
  weight = weightFor(size * (1 - inset * 2)),
  tip = weight !== 'tiny',
  id = 'ri',
}: { size: number; inset?: number; round?: boolean; square?: boolean; weight?: MarkWeight; tip?: boolean; id?: string }): string {
  const sh = shapes(weight);
  const scale = 1 - inset * 2;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="${size}" height="${size}" aria-hidden="true">` +
    '<defs>' +
    `<linearGradient id="${id}g" x1="0" y1="0" x2="100" y2="100" gradientUnits="userSpaceOnUse"><stop offset="0" stop-color="${ICON_STOPS[0]}"/><stop offset="0.55" stop-color="${ICON_STOPS[1]}"/><stop offset="1" stop-color="${ICON_STOPS[2]}"/></linearGradient>` +
    cutOut(id, sh.letter) +
    '</defs>' +
    (square ? `<rect width="100" height="100" rx="${round ? 22.5 : 0}" fill="url(#${id}g)"/>` : '') +
    `<g transform="translate(${n(inset * 100)} ${n(inset * 100)}) scale(${n(scale * 1000) / 1000})">` +
    `<g${sh.letter ? ` mask="url(#${id}m)"` : ''} fill="#fff" stroke="#fff">${sh.rim}<g stroke="none">${sh.body}</g></g>` +
    (tip ? `<rect x="${sh.tip.x}" y="${sh.tip.y}" width="${sh.tip.w}" height="${sh.tip.h}" rx="1.4" fill="${MARK_COLORS.light.tip}"/>` : '') +
    '</g></svg>'
  );
}
